/**
 * Notification bell icon with unread count badge and upward-opening popover tray.
 * Sits in the sidebar footer next to the user profile.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, Bell, CheckCheck, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useNotificationsStore, type Notification } from "@/lib/notifications";
import { formatRelativeTime } from "@/lib/licensing/project-stage";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/use-toast";

// ─────────────────────────────────────────────────────────────────────────────
// Notification type icons/colors
// ─────────────────────────────────────────────────────────────────────────────

const TYPE_CONFIG: Record<string, { dot: string }> = {
  rights_holder_added: { dot: "bg-purple-400" },
  license_request_received: { dot: "bg-blue-400" },
  license_request_responded: { dot: "bg-green-400" },
  license_request_message: { dot: "bg-blue-400" },
  license_counter_offer: { dot: "bg-amber-400" },
  license_request_withdrawn: { dot: "bg-red-400" },
  rights_finalized: { dot: "bg-emerald-400" },
  // C2 repair layer (2026-07-08): holder joined a negotiation lane / party disavowed a side.
  license_participation_confirmed: { dot: "bg-emerald-400" },
  license_side_disavowed: { dot: "bg-red-400" },
  // Holder-confirm gate (2026-07-10): buyer named an org as a rights holder / org declined the tag.
  license_holder_seeded: { dot: "bg-purple-400" },
  license_holder_declined: { dot: "bg-red-400" },
  // Deal close (2026-07-11): buyer accepted the deal — approver orgs learn out-of-console.
  license_deal_accepted: { dot: "bg-emerald-400" },
  // Added 2026-08-08 with the notification itself — a decline told nobody before that.
  license_deal_declined: { dot: "bg-red-400" },
  // Added 2026-09-24 with the notification itself: a holder's decline that closes the WHOLE deal
  // now tells the other parties. Red like license_deal_declined — same outcome (the deal is over),
  // it just came from the seller side.
  license_deal_rejected_by_holder: { dot: "bg-red-400" },
  // Added 2026-09-28 with the notification itself: the buyer paid. Green like an accepted deal —
  // good news, and the cue for holders and the coordinator to finish their side.
  license_deal_paid: { dot: "bg-emerald-400" },
  // Added 2026-09-29: a buyer was charged twice for one deal (admins only). Rose: money owed back.
  license_deal_second_payment: { dot: "bg-rose-400" },
  // Added 2026-10-02: a paid deal needs an admin's decision — part of its payment refunded in
  // Stripe, or paid while a holder lane had no contract (admins only). Amber: nothing was undone.
  license_deal_payment_issue: { dot: "bg-amber-400" },
  // Added 2026-08-27 with the notification itself: the four term-changing paths emitted nothing
  // at all until then (reported by Believe). Amber matches license_counter_offer — same family,
  // a change to the terms on the table that the counterparty has not agreed to yet.
  license_terms_updated: { dot: "bg-amber-400" },
  // Added 2026-09-30 with their notices (notification_service.py, "Your move" notices for the
  // deal's later stages; notify_license_request_track_declined). Without an entry each fell back to
  // the grey `system` dot, which reads as "unclassified". Coloured, like the entries above, by
  // what HAPPENED (not by whose move it is), reusing the existing dot styles:
  //   emerald the deal moved forward: every side approved, the buyer accepted (the contract-needed
  //           notice is the coordinator's news of that acceptance, as license_deal_accepted is the
  //           approvers'), contracts attached, paid, files released;
  //   amber   something is still open and unresolved (as license_terms_updated);
  //   red     something is over (as license_deal_declined).
  // The side-opened notice reuses license_participation_confirmed (above), so it needs no entry.
  // A reopened side and a decision taken back from the buyer are amber: the deal is not over, but a
  // party that had finished must act again.
  license_deal_decision_open: { dot: "bg-emerald-400" },
  license_deal_coverage_open: { dot: "bg-amber-400" },
  license_deal_contract_needed: { dot: "bg-emerald-400" },
  license_deal_contracts_complete: { dot: "bg-emerald-400" },
  license_deal_payment_received: { dot: "bg-emerald-400" },
  license_deal_files_released: { dot: "bg-emerald-400" },
  license_deal_side_reopened: { dot: "bg-amber-400" },
  license_deal_decision_withdrawn: { dot: "bg-amber-400" },
  license_request_track_declined: { dot: "bg-red-400" },
  // Added 2026-10-26 with the contract round trip (notify_contract_sent / _changes_requested /
  // _signed, spec 2026-10-26-contracts-sign-return-design.md §6.2): a version sent and a signed copy
  // move the deal forward; a change request leaves the contract open until the holder answers.
  license_deal_contract_sent: { dot: "bg-emerald-400" },
  license_deal_contract_changes_requested: { dot: "bg-amber-400" },
  license_deal_contract_signed: { dot: "bg-emerald-400" },
  // Buyer projects, Phase B (2026-10-09): a teammate @mentioned you, a project went to sellers,
  // sellers replied. Purple is the project family's colour.
  project_comment_mention: { dot: "bg-purple-400" },
  project_submitted: { dot: "bg-purple-400" },
  project_status_changed: { dot: "bg-emerald-400" },
  // Phase C: a reviewer (client) reacted or left a note through a review link.
  project_client_feedback: { dot: "bg-amber-400" },
  system: { dot: "bg-muted-foreground" },
};

/**
 * The unread-dot class for a notification type: its TYPE_CONFIG entry, or the grey `system` dot for
 * a type the bell does not know. Own keys only, so a type named like an inherited property
 * ("toString", "constructor") gets the fallback rather than a function.
 */
export function notificationDotClass(type: string | null | undefined): string {
  const key = String(type ?? "");
  const cfg = Object.prototype.hasOwnProperty.call(TYPE_CONFIG, key)
    ? TYPE_CONFIG[key]
    : TYPE_CONFIG.system;
  return cfg.dot;
}

/**
 * What the empty tray says. The tray lists UNREAD notices only (fetchNotifications asks for
 * `unread_only=true`, and a click or "Mark all read" removes a notice from it), so an empty tray
 * means nothing is unread, not that nothing ever arrived. It said "No notifications yet".
 */
export const NOTIFICATION_TRAY_EMPTY_TEXT = "No unread notifications";

/**
 * What the tray says when the list could not be read. An empty list after a failed read proves
 * nothing, so it must not say "No unread notifications" (it did, next to a badge counting them).
 */
export const NOTIFICATION_TRAY_FAILED_TEXT = "Couldn't load your notifications";

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

interface NotificationBellProps {
  /** Optional custom trigger. Receives unreadCount for badge rendering. */
  renderTrigger?: (unreadCount: number) => React.ReactNode;
}

export function NotificationBell({ renderTrigger }: NotificationBellProps = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const {
    notifications,
    unreadCount,
    loading,
    loadedOnce,
    loadFailed,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    subscribeToRealtime,
  } = useNotificationsStore();

  // Fetch on mount + subscribe to realtime
  useEffect(() => {
    if (!user?.id) return;

    fetchUnreadCount();

    const unsubscribe = subscribeToRealtime(user.id);

    return unsubscribe;
  }, [user?.id, fetchUnreadCount, subscribeToRealtime]);

  // Toast on new notification (via realtime)
  useEffect(() => {
    if (!loadedOnce) return;
    // The latest notification — if it just arrived and is unread
    const latest = notifications[0];
    if (latest && !latest.is_read) {
      // Only toast if it's very recent (within last 5 seconds)
      const age = Date.now() - new Date(latest.created_at).getTime();
      if (age < 5000) {
        toast({
          title: latest.title,
          description: latest.message || undefined,
        });
      }
    }
  }, [notifications[0]?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close and go at once; markAsRead is optimistic (and reverts on failure), so nothing waits on it.
  const handleNotificationClick = (notification: Notification) => {
    setOpen(false);
    if (notification.action_url) navigate(notification.action_url);
    if (!notification.is_read) void markAsRead(notification.id);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next && !loadedOnce) {
      fetchNotifications();
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {renderTrigger ? (
          <button aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}>
            {renderTrigger(unreadCount)}
          </button>
        ) : (
          <button
            className="relative p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground hover:text-foreground"
            aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
          >
            <Bell className="h-4 w-4" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold flex items-center justify-center">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="end"
        className="w-[360px] p-0"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="text-sm font-semibold">Notifications</h3>
          {unreadCount > 0 && (
            <button
              onClick={() => markAllAsRead()}
              className="text-[10px] text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors"
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </button>
          )}
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[400px]">
          {loading && !loadedOnce ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : loadFailed ? (
            <div className="py-8 text-center">
              <AlertCircle className="h-6 w-6 text-sh-bad mx-auto mb-2" aria-hidden="true" />
              <p className="text-sm text-muted-foreground">{NOTIFICATION_TRAY_FAILED_TEXT}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => fetchNotifications()}
              >
                Retry
              </Button>
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-8 text-center">
              <Bell className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{NOTIFICATION_TRAY_EMPTY_TEXT}</p>
            </div>
          ) : (
            <div>
              {notifications.map((notification) => {
                const dotClass = notificationDotClass(notification.type);
                return (
                  <button
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={cn(
                      "w-full text-left px-4 py-3 hover:bg-accent/50 transition-colors border-b border-border/30 last:border-0",
                      !notification.is_read && "bg-primary/[0.03]"
                    )}
                  >
                    <div className="flex gap-3">
                      {/* Unread dot */}
                      <div className="pt-1.5 flex-shrink-0">
                        <div className={cn(
                          "w-2 h-2 rounded-full",
                          notification.is_read ? "bg-transparent" : dotClass
                        )} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={cn(
                          "text-sm line-clamp-2 break-words",
                          !notification.is_read ? "font-medium" : "text-muted-foreground"
                        )}>
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-[13px] text-muted-foreground mt-0.5 line-clamp-2">
                            {notification.message}
                          </p>
                        )}
                        <time
                          dateTime={notification.created_at}
                          title={new Date(notification.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                          className="block text-[13px] text-muted-foreground mt-1"
                        >
                          {formatRelativeTime(notification.created_at) ?? ""}
                        </time>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
